import type { BondFlow } from "@/lib/brokerBonds";

// The three presentational rules every list page in the portal repeats: how a
// timestamp reads in a table cell, and how a status maps onto a badge's palette
// and label. Extracted when the bond-order list (BRK-S088) would have been the
// THIRD copy — a repeated label-map is a smell (CLAUDE.md, Coding Standards).
//
// These are aesthetic mappings only; nothing here decides behaviour, and an
// unknown status degrades to a neutral chip rather than disappearing.

/** A created/updated timestamp as a plain `YYYY-MM-DD` — stable and
 *  locale-neutral, which is what a sortable table column wants. */
export function formatListDate(iso: string): string {
  return iso.slice(0, 10);
}

/** The "this finished well" chip, shared by the statuses that mean it. */
const DONE_BADGE = "bg-green-100 text-green-700";

/** Status → badge palette. Unknown statuses fall back to a neutral grey chip. */
export function statusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    // A bond request's DECISION shares this palette rather than owning a second
    // one (BRK-S174): `approved` is the same "this finished well" chip as a
    // bound policy, `referred` already maps to the amber it should, and
    // `pending` falls through to the neutral default — which is correct without
    // a case of its own, because a decision nobody has made yet is not a state
    // to colour.
    case "approved":
    case "bound":
    case "active":
      return DONE_BADGE;
    case "quoted":
    case "in_progress":
    case "submitted":
      return "bg-blue-100 text-blue-700";
    case "declined":
    case "error":
      return "bg-brk-accent/10 text-brk-accent";
    case "referred":
    case "waiting_on_carrier":
      return "bg-amber-100 text-amber-700";
    case "expired":
    case "cancelled":
    case "draft":
    default:
      return "bg-brk-bg text-brk-dim";
  }
}

/**
 * A `snake_case` key as Title Case (`bond_indemnity` → "Bond Indemnity").
 *
 * The last-resort label for a STATUS this bundle has never seen. Extracted from
 * `statusLabel` so the two humanising styles live side by side and their
 * difference is visible — see `sentenceCaseKey` below, which is the one a
 * document type's label falls back to, and why.
 */
export function titleCaseKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A key as a SENTENCE (`surety_rider_addendum` → "Surety rider addendum",
 * `bidDocuments` → "Bid documents").
 *
 * The other humanising style, and the difference from `titleCaseKey` is not
 * cosmetic: this one is the fallback for a LABEL, and PMS's document registry
 * writes labels as sentences ("Bank letter", "Prior bond form"), so a Title
 * Case fallback would print a label that reads unlike every one beside it.
 * `titleCaseKey` stays the style for a status BADGE, where every other chip is
 * Title Case.
 *
 * Handles `_`/`-` AND camelCase, because a document type key is snake_case and
 * a configuration field key is camelCase, and one regex for both would print
 * "Biddocuments".
 */
export function sentenceCaseKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (spaced === "") return key;
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Status → its human label (`waiting_on_carrier` → "Waiting on Carrier"). */
export function statusLabel(status: string): string {
  if (status === "waiting_on_carrier") return "Waiting on Carrier";
  return titleCaseKey(status);
}

// ── A bond order's status reads differently per FLOW (BRK-S160) ──────────────
//
// `submitted` is one value on the wire and two facts on screen. A bond request
// that is `submitted` has been **Filed with Signal** — somebody is going to
// underwrite it and the broker is waiting. A standalone pre-approval that is
// `submitted` is **Complete**: the decision has landed, it lives on the
// insured's Surety program block, and there is nothing left to wait for. Saying
// "Submitted" for the second would leave a broker watching a list for an answer
// they already have.
//
// Both halves live here rather than at each surface, so the label and the chip
// colour cannot disagree — three surfaces render this row.

/** Is this a finished standalone pre-approval, rather than a filed request? */
function isCompletedPreapproval(status: string, flow: BondFlow): boolean {
  return flow === "preapproval" && status === "submitted";
}

/** A bond order's status label, in the words its FLOW makes true. */
export function bondStatusLabel(
  status: string,
  flow: BondFlow = "bond",
): string {
  return isCompletedPreapproval(status, flow)
    ? "Complete"
    : statusLabel(status);
}

/** The chip for that label — green for a completed pre-approval, because it is
 *  an ending rather than a hand-off. */
export function bondStatusBadgeClass(
  status: string,
  flow: BondFlow = "bond",
): string {
  return isCompletedPreapproval(status, flow)
    ? DONE_BADGE
    : statusBadgeClass(status);
}
